import React, { useState, useEffect, useMemo } from 'react';
import {
  SparklesIcon,
  CheckCircleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  AcademicCapIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  QuestionMarkCircleIcon,
  TagIcon,
  XMarkIcon,
  CheckBadgeIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import {
  WeeklyQuery,
  WeeklyQueryStatus,
  WeeklyQueryStats,
  MondayFilterOption,
} from '../../types';
import {
  WEEKLY_QUERY_CATEGORIES,
  getWeekIdentifier,
  subscribeToWeeklyQueries,
  updateWeeklyQueryStatus,
  autoSortWeeklyQueries,
  computeWeeklyQueryStats,
  exportWeeklyQueriesToExcel,
  isQueryInMondayWeek,
  formatMondayDisplayName,
  getMondayFilterOptions,
  seedSampleWeeklyQueries,
} from '../../services/firebaseService';

interface WeeklyQueriesInstructorViewProps {
  instructor: any;
}

const WeeklyQueriesInstructorView: React.FC<WeeklyQueriesInstructorViewProps> = ({ instructor }) => {
  const [allQueries, setAllQueries] = useState<WeeklyQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState<string>(getWeekIdentifier());
  const [sortMode, setSortMode] = useState<'smart_cluster' | 'category' | 'agenda' | 'newest'>('smart_cluster');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [seedingData, setSeedingData] = useState(false);

  // Action modal / inline note editor
  const [activeQueryForAction, setActiveQueryForAction] = useState<WeeklyQuery | null>(null);
  const [actionType, setActionType] = useState<'plan' | 'address' | null>(null);
  const [actionNotes, setActionNotes] = useState('');
  const [actionSaving, setActionSaving] = useState(false);

  // Subscribe to all weekly queries in real time
  useEffect(() => {
    setLoading(true);
    const unsubscribe = subscribeToWeeklyQueries((queries) => {
      setAllQueries(queries);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Compute Monday filter options with counts and relative labels
  const mondayOptions: MondayFilterOption[] = useMemo(() => {
    return getMondayFilterOptions(allQueries);
  }, [allQueries]);

  // Filter queries based on selected Monday week, category, status, and search query
  const filteredQueries = useMemo(() => {
    let list = allQueries;

    if (selectedWeek !== 'all') {
      list = list.filter((q) => isQueryInMondayWeek(q, selectedWeek));
    }

    if (categoryFilter !== 'all') {
      list = list.filter((q) => q.category === categoryFilter);
    }

    if (statusFilter !== 'all') {
      list = list.filter((q) => q.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const qLower = searchQuery.toLowerCase().trim();
      list = list.filter(
        (q) =>
          q.studentName.toLowerCase().includes(qLower) ||
          q.studentRegNoSuffix.includes(qLower) ||
          q.studentRegistrationNumber.toLowerCase().includes(qLower) ||
          q.questionText.toLowerCase().includes(qLower) ||
          (q.similarityCluster && q.similarityCluster.toLowerCase().includes(qLower)) ||
          (q.instructorNotes && q.instructorNotes.toLowerCase().includes(qLower))
      );
    }

    return autoSortWeeklyQueries(list, sortMode);
  }, [allQueries, selectedWeek, categoryFilter, statusFilter, searchQuery, sortMode]);

  // Compute stats for the currently selected week (or all)
  const currentWeekQueries = useMemo(() => {
    if (selectedWeek === 'all') return allQueries;
    return allQueries.filter((q) => isQueryInMondayWeek(q, selectedWeek));
  }, [allQueries, selectedWeek]);

  const stats: WeeklyQueryStats = useMemo(() => {
    return computeWeeklyQueryStats(currentWeekQueries);
  }, [currentWeekQueries]);

  // Quick seed handler for testing & demoing Monday queries
  const handleSeedData = async () => {
    setSeedingData(true);
    try {
      const added = await seedSampleWeeklyQueries();
      alert(`Created ${added} sample student doubts across This Week, Last Week, and 2 Weeks Ago! You can now filter by each Monday.`);
    } catch (err) {
      console.error('Failed to seed sample doubts:', err);
      alert('Error creating sample doubts.');
    } finally {
      setSeedingData(false);
    }
  };

  // Handle plan for next class action
  const openActionModal = (query: WeeklyQuery, type: 'plan' | 'address') => {
    setActiveQueryForAction(query);
    setActionType(type);
    setActionNotes(query.instructorNotes || '');
  };

  const handleSaveAction = async () => {
    if (!activeQueryForAction?.id) return;
    setActionSaving(true);
    try {
      const nextStatus: WeeklyQueryStatus =
        actionType === 'address' ? 'addressed_in_class' : 'planned_for_class';

      await updateWeeklyQueryStatus(activeQueryForAction.id, {
        status: nextStatus,
        instructorId: instructor?.uid || instructor?.id || 'instructor',
        instructorName: instructor?.name || instructor?.displayName || 'Lead Instructor',
        instructorNotes: actionNotes.trim(),
      });

      setActiveQueryForAction(null);
      setActionType(null);
      setActionNotes('');
    } catch (err) {
      console.error('Failed to update query status:', err);
      alert('Error updating query. Please try again.');
    } finally {
      setActionSaving(false);
    }
  };

  const handleExportExcel = () => {
    exportWeeklyQueriesToExcel(
      filteredQueries,
      selectedWeek === 'all' ? 'All_Weeks' : selectedWeek
    );
  };

  return (
    <div className="space-y-6">
      {/* Top Banner / Week Context */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 border border-slate-800 rounded-3xl p-5 sm:p-7 shadow-xl">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
          <div className="space-y-1.5">
            <div className="flex items-center space-x-2.5">
              <span className="px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 uppercase flex items-center space-x-1.5">
                <AcademicCapIcon className="w-3.5 h-3.5" />
                <span>Instructor Teaching Console</span>
              </span>
              <span className="text-xs text-slate-400">
                Sorted Automatically by Relevance & Priority
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight">
              Weekly Student Queries & Lecture Agenda
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 max-w-2xl leading-relaxed">
              Enrolled students submit up to 3 questions each Monday. Review clustered topics, mark queries to address in upcoming classes next week, and document lecture talking points for your live broadcast!
            </p>
          </div>

          {/* Week Selector Dropdown & Quick Actions */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="bg-slate-800/80 border border-slate-700 rounded-2xl px-3.5 py-2 flex items-center space-x-2">
              <CalendarDaysIcon className="w-4 h-4 text-indigo-400" />
              <span className="text-xs text-slate-400 font-medium">Monday Cohort:</span>
              <select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                className="bg-transparent text-white font-bold text-xs sm:text-sm focus:outline-none cursor-pointer max-w-[220px] truncate"
              >
                <option value="all" className="bg-slate-900 text-white">All Recorded Mondays ({allQueries.length})</option>
                {mondayOptions.map((m) => (
                  <option key={m.id} value={m.id} className="bg-slate-900 text-white">
                    {m.fullLabel} {m.count !== undefined ? `— [${m.count} Doubts]` : ''}
                  </option>
                ))}
              </select>
            </div>

            {allQueries.length === 0 && (
              <button
                onClick={handleSeedData}
                disabled={seedingData}
                className="px-3.5 py-2.5 rounded-2xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 text-xs font-bold transition flex items-center space-x-1.5 cursor-pointer shadow-sm"
                title="Populate test student doubts for This Week, Last Week & 2 Weeks Ago"
              >
                <SparklesIcon className="w-4 h-4 text-amber-400" />
                <span>{seedingData ? 'Generating...' : 'Seed Sample Doubts'}</span>
              </button>
            )}

            <button
              onClick={handleExportExcel}
              className="px-4 py-2.5 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition flex items-center space-x-2 shadow-lg shadow-indigo-600/20 active:scale-95 cursor-pointer"
              title="Export query report and talking points to Excel"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span>Export Excel</span>
            </button>
          </div>
        </div>

        {/* Dedicated Monday Quick-Filter Bar */}
        <div className="mt-5 pt-4 border-t border-slate-800/80">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-3">
            <div className="flex items-center space-x-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-300 flex items-center space-x-1.5">
                <CalendarDaysIcon className="w-3.5 h-3.5 text-indigo-400" />
                <span>Select Monday Cohort (हफ्ते के हिसाब से फ़िल्टर करें):</span>
              </span>
            </div>
            <span className="text-[11px] text-slate-400">
              Showing doubts submitted on selected Mondays
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* All Mondays Pill */}
            <button
              type="button"
              onClick={() => setSelectedWeek('all')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center space-x-2 border cursor-pointer ${selectedWeek === 'all'
                  ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white border-indigo-400/50 shadow-md shadow-indigo-950/40'
                  : 'bg-slate-800/80 hover:bg-slate-800 text-slate-300 border-slate-700'
                }`}
            >
              <span>All Recorded Mondays</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold ${selectedWeek === 'all'
                  ? 'bg-white/20 text-white'
                  : 'bg-slate-700 text-slate-300'
                }`}>
                {allQueries.length}
              </span>
            </button>

            {/* Individual Monday Filter Buttons (This Week, Last Week, 2 Weeks Ago, etc.) */}
            {mondayOptions.slice(0, 5).map((m) => {
              const isSelected = selectedWeek === m.id || selectedWeek === m.mondayDate;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setSelectedWeek(m.id)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center space-x-2 border cursor-pointer ${isSelected
                      ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white border-indigo-400/50 shadow-md shadow-indigo-950/40'
                      : 'bg-slate-800/80 hover:bg-slate-800 text-slate-300 border-slate-700'
                    }`}
                >
                  <span>{m.relativeLabel}</span>
                  <span className="text-[11px] opacity-75 font-normal">
                    ({m.formattedDate.replace(/^[A-Za-z]+, /, '')})
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold ${isSelected
                      ? 'bg-white/20 text-white'
                      : m.count && m.count > 0
                        ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-500/30'
                        : 'bg-slate-700 text-slate-400'
                    }`}>
                    {m.count || 0}
                  </span>
                </button>
              );
            })}

            {/* Dropdown if more than 5 past Mondays */}
            {mondayOptions.length > 5 && (
              <select
                value={mondayOptions.slice(5).some(m => m.id === selectedWeek || m.mondayDate === selectedWeek) ? selectedWeek : ''}
                onChange={(e) => {
                  if (e.target.value) setSelectedWeek(e.target.value);
                }}
                className="bg-slate-800/90 border border-slate-700 text-slate-300 text-xs font-bold rounded-xl px-3 py-2 focus:outline-none cursor-pointer"
              >
                <option value="">More Previous Mondays...</option>
                {mondayOptions.slice(5).map((m) => (
                  <option key={m.id} value={m.id} className="bg-slate-900 text-white">
                    {m.fullLabel} ({m.count || 0})
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* 4 Key Statistics Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6">
          <div className="p-4 rounded-2xl bg-slate-800/60 border border-slate-700/60 flex flex-col justify-between">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Total Questions
            </span>
            <div className="flex items-baseline space-x-2 mt-1">
              <span className="text-2xl sm:text-3xl font-black text-white">
                {stats.totalQueries}
              </span>
              <span className="text-xs text-slate-400">
                from {stats.uniqueStudents} candidate{stats.uniqueStudents !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex flex-col justify-between">
            <span className="text-[11px] font-bold text-amber-300 uppercase tracking-wider flex items-center space-x-1">
              <ClockIcon className="w-3.5 h-3.5" />
              <span>Pending Review</span>
            </span>
            <div className="mt-1">
              <span className="text-2xl sm:text-3xl font-black text-amber-400">
                {stats.pendingCount}
              </span>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/30 flex flex-col justify-between">
            <span className="text-[11px] font-bold text-blue-300 uppercase tracking-wider flex items-center space-x-1">
              <AcademicCapIcon className="w-3.5 h-3.5" />
              <span>Scheduled for Next Class</span>
            </span>
            <div className="mt-1">
              <span className="text-2xl sm:text-3xl font-black text-blue-400">
                {stats.plannedForClassCount}
              </span>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex flex-col justify-between">
            <span className="text-[11px] font-bold text-emerald-300 uppercase tracking-wider flex items-center space-x-1">
              <CheckCircleIcon className="w-3.5 h-3.5" />
              <span>Addressed in Class</span>
            </span>
            <div className="mt-1">
              <span className="text-2xl sm:text-3xl font-black text-emerald-400">
                {stats.addressedCount}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter, Search & Automated Sort Controls */}
      <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
        {/* Search Input */}
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by student name, ID, question keyword, or cluster..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-800/80 border border-slate-700 text-white text-xs placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter & Sort Selectors */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Automatic Sort Mode Selector */}
          <div className="flex items-center space-x-1.5 bg-slate-800/90 border border-indigo-500/40 rounded-xl px-3 py-1.5">
            <SparklesIcon className="w-3.5 h-3.5 text-warning-400" />
            <span className="text-xs text-slate-300 font-semibold">Sort:</span>
            <select
              value={sortMode}
              onChange={(e: any) => setSortMode(e.target.value)}
              className="bg-transparent text-white text-xs font-bold focus:outline-none cursor-pointer"
            >
              <option value="smart_cluster" className="bg-slate-900 text-white">⚡ Auto-Sort (Topic Clustering)</option>
              <option value="agenda" className="bg-slate-900 text-white">🎯 Next Class Agenda First</option>
              <option value="category" className="bg-slate-900 text-white">📁 Group by Category</option>
              <option value="newest" className="bg-slate-900 text-white">🕒 Newest First</option>
            </select>
          </div>

          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-xl px-3 py-2 focus:outline-none cursor-pointer"
          >
            <option value="all">All Categories</option>
            {WEEKLY_QUERY_CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.icon} {c.label.split('&')[0]}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-xl px-3 py-2 focus:outline-none cursor-pointer"
          >
            <option value="all">All Statuses</option>
            <option value="pending">⏳ Pending Review</option>
            <option value="planned_for_class">🎯 Scheduled for Next Class</option>
            <option value="addressed_in_class">✅ Addressed in Class</option>
          </select>
        </div>
      </div>

      {/* Main Questions List */}
      <div className="space-y-4">
        {loading ? (
          <div className="py-16 text-center text-slate-400 flex flex-col items-center justify-center space-y-2">
            <ArrowPathIcon className="w-8 h-8 animate-spin text-indigo-400" />
            <p className="text-xs">Loading questions submitted by students...</p>
          </div>
        ) : filteredQueries.length === 0 ? (
          <div className="p-12 text-center rounded-3xl bg-slate-900/60 border border-slate-800 text-slate-400 space-y-2">
            <QuestionMarkCircleIcon className="w-10 h-10 text-slate-500 mx-auto" />
            <h4 className="text-base font-bold text-white">No Questions Found</h4>
            <p className="text-xs max-w-sm mx-auto">
              No questions matched the selected week, category, or search filters.
            </p>
          </div>
        ) : (
          filteredQueries.map((query, index) => {
            const isPlanned = query.status === 'planned_for_class';
            const isAddressed = query.status === 'addressed_in_class';

            return (
              <div
                key={query.id || index}
                className={`p-5 rounded-3xl border transition-all duration-200 ${isPlanned
                    ? 'bg-gradient-to-r from-blue-950/40 via-indigo-950/30 to-slate-900 border-blue-500/50 shadow-lg shadow-blue-950/20'
                    : isAddressed
                      ? 'bg-slate-900/60 border-slate-800 opacity-90'
                      : 'bg-slate-900/80 border-slate-800 hover:border-slate-700'
                  }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  {/* Left Column: Student info, category, and question text */}
                  <div className="space-y-3 flex-1">
                    {/* Tags row */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                        ID: {query.studentRegNoSuffix}
                      </span>
                      <span className="text-xs font-bold text-white">
                        {query.studentName}
                      </span>
                      {query.studentBatchLabel && (
                        <span className="text-[11px] text-slate-400">
                          &bull; {query.studentBatchLabel}
                        </span>
                      )}

                      {/* Clear Monday Cohort Badge */}
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-500/10 text-indigo-300 border border-indigo-500/30 flex items-center space-x-1">
                        <CalendarDaysIcon className="w-3 h-3 text-indigo-400" />
                        <span>Monday: {formatMondayDisplayName(query.weekMondayDate || query.weekIdentifier)}</span>
                      </span>

                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-800 text-slate-300 border border-slate-700">
                        {query.categoryLabel || query.category}
                      </span>

                      {query.similarityCluster && (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-950/80 text-indigo-200 border border-indigo-500/30 flex items-center space-x-1">
                          <TagIcon className="w-3 h-3 text-indigo-400" />
                          <span>Cluster: {query.similarityCluster}</span>
                        </span>
                      )}

                      <span className="text-[10px] font-mono text-slate-500">
                        Q#{query.questionNumber} of 3
                      </span>
                    </div>

                    {/* Question text box */}
                    <div className="p-3.5 rounded-2xl bg-black/30 border border-slate-800 text-white text-xs sm:text-sm leading-relaxed">
                      "{query.questionText}"
                    </div>

                    {/* Existing Instructor Talking Points or Notes */}
                    {query.instructorNotes && (
                      <div className="p-3 rounded-2xl bg-indigo-950/70 border border-indigo-500/40 text-xs space-y-1">
                        <div className="flex items-center space-x-1.5 text-indigo-300 font-bold">
                          <AcademicCapIcon className="w-4 h-4" />
                          <span>
                            Class Talking Point {query.instructorName ? `(${query.instructorName})` : ''}:
                          </span>
                        </div>
                        <p className="text-slate-200 pl-5">
                          {query.instructorNotes}
                        </p>
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-4 text-[11px] text-slate-400">
                      <span>Submitted: {query.submittedAtStr || 'Recently'}</span>
                      <span>Target: {query.targetClassWeek || 'Upcoming Classes'}</span>
                      {query.addressedAt && (
                        <span className="text-emerald-400">
                          &bull; Addressed: {new Date(query.addressedAt?.toDate?.() || query.addressedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right Column: Status & Instructor Action Buttons */}
                  <div className="flex flex-row lg:flex-col items-center lg:items-end justify-between lg:justify-start gap-3 flex-shrink-0">
                    <div>
                      {isPlanned ? (
                        <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-blue-500/20 text-blue-300 border border-blue-500/40 shadow-sm flex items-center space-x-1.5">
                          <CheckBadgeIcon className="w-4 h-4 text-blue-400" />
                          <span>Selected for Next Class</span>
                        </span>
                      ) : isAddressed ? (
                        <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-sm flex items-center space-x-1.5">
                          <CheckCircleIcon className="w-4 h-4 text-emerald-400" />
                          <span>Addressed in Live Class</span>
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center space-x-1.5">
                          <ClockIcon className="w-4 h-4 text-amber-400" />
                          <span>Pending Review</span>
                        </span>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => openActionModal(query, 'plan')}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center space-x-1 border ${isPlanned
                            ? 'bg-blue-600/30 hover:bg-blue-600/50 text-blue-200 border-blue-400/30'
                            : 'bg-indigo-600 hover:bg-indigo-500 text-white border-transparent shadow'
                          }`}
                        title="Schedule to address this doubt in upcoming classes"
                      >
                        <AcademicCapIcon className="w-3.5 h-3.5" />
                        <span>{isPlanned ? 'Edit Class Note' : 'Select for Next Class'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => openActionModal(query, 'address')}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center space-x-1 border ${isAddressed
                            ? 'bg-emerald-600/30 text-emerald-200 border-emerald-400/30'
                            : 'bg-emerald-600 hover:bg-emerald-500 text-white border-transparent shadow'
                          }`}
                        title="Mark this query as addressed in class"
                      >
                        <CheckCircleIcon className="w-3.5 h-3.5" />
                        <span>{isAddressed ? 'Update Answer' : 'Mark Addressed'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Instructor Action Modal for adding talking points / addressing */}
      {activeQueryForAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl p-6 w-full max-w-lg shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center space-x-2.5">
                <AcademicCapIcon className="w-6 h-6 text-indigo-400" />
                <h3 className="text-base sm:text-lg font-bold text-white">
                  {actionType === 'address'
                    ? 'Mark Query as Addressed in Class'
                    : 'Schedule for Upcoming Class Agenda'}
                </h3>
              </div>
              <button
                onClick={() => setActiveQueryForAction(null)}
                className="text-slate-400 hover:text-white"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-3.5 rounded-2xl bg-slate-800/60 border border-slate-700/60 space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-white">{activeQueryForAction.studentName}</span>
                <span className="text-slate-400 font-mono">ID: {activeQueryForAction.studentRegNoSuffix}</span>
              </div>
              <p className="text-xs text-slate-300">
                "{activeQueryForAction.questionText}"
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1.5">
                {actionType === 'address'
                  ? 'Resolution / Lecture Reference Notes (Visible to Student & Admins)'
                  : 'Lecture Talking Points / Class Plan (What will you cover in class?)'}
              </label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                rows={4}
                placeholder={
                  actionType === 'address'
                    ? 'e.g. Demonstrated proper posture & cadence during the Wednesday 1600m training session.'
                    : 'e.g. Will explain stride frequency & pre-workout banana/water intake during Tuesday live lecture.'
                }
                className="w-full px-3.5 py-2.5 rounded-2xl bg-slate-800 border border-slate-700 text-white text-xs sm:text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setActiveQueryForAction(null)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-300 hover:bg-slate-800 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAction}
                disabled={actionSaving}
                className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs shadow-lg transition flex items-center space-x-2 disabled:opacity-50 cursor-pointer"
              >
                {actionSaving ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>
                    {actionType === 'address' ? 'Confirm Addressed' : 'Save to Class Agenda'}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyQueriesInstructorView;
